import { t } from './LocaleContext';
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { getToken, setToken, removeToken } from '../utils/storage';
import api from '../api';
import { useLocale } from './LocaleContext';

const BACKEND_ROLES = ['admin', 'ops', 'salesperson', 'sales_manager', 'technician', 'tech_manager', 'finance', 'clerk', 'purchaser', 'factory'];

function isStoreOnlyAccount(roles: string[]): boolean {
  if (!roles || roles.length === 0) return false;
  return !roles.some((r) => BACKEND_ROLES.includes(r));
}

export interface User {
  id: number;
  username: string;
  real_name: string;
  email: string;
  roles: string[];
  permissions?: {
    menus: string[];
    buttons: string[];
  };
  lang: string;
  avatar?: string;
  store_id?: number;
  store_ids?: number[];
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  hasRole: (roles: string[]) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  // 权限配置控制（新）
  hasMenuPermission: (menuKey: string) => boolean;
  hasButtonPermission: (buttonCode: string) => boolean;
  // 页面级别权限控制
  canViewOrder: (status: number) => boolean;
  canReviewFirst: () => boolean;
  canReviewSecond: () => boolean;
  canReviewFinance: () => boolean;
  canPushToFactory: () => boolean;
  canShipOrder: () => boolean;
  canCompleteOrder: () => boolean;
  isAdmin: () => boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  login: async () => {},
  logout: () => {},
  hasRole: () => false,
  hasAnyRole: () => false,
  hasMenuPermission: () => false,
  hasButtonPermission: () => false,
  canViewOrder: () => false,
  canReviewFirst: () => false,
  canReviewSecond: () => false,
  canReviewFinance: () => false,
  canPushToFactory: () => false,
  canShipOrder: () => false,
  canCompleteOrder: () => false,
  isAdmin: () => false,
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useLocale();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const res: any = await api.get('/auth/me');
      if (res?.code === 0) {
        const roles = res.data?.roles || [];
        // 已登录的门店账号自动踢出
        if (isStoreOnlyAccount(roles)) {
          logout();
          return;
        }
        // 确保 roles 始终是数组
        setUser({ ...res.data, roles });
      }
    } catch (err: any) {
      // 仅对认证类错误清除 token；网络/500 等临时错误保留 token，避免误登出
      const status = err?.status;
      const code = err?.code;
      const isAuthError =
        status === 401 ||
        status === 403 ||
        code === 401001 ||
        code === 401002 ||
        code === 400005;
      if (isAuthError) {
        removeToken();
      } else {
        // eslint-disable-next-line no-console
        console.warn('[AuthContext] /auth/me failed, keep token:', err?.message || err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (getToken()) {
      fetchMe();
    } else {
      setLoading(false);
    }
  }, [fetchMe]);

  const login = async (username: string, password: string) => {
    const res: any = await api.post('/auth/login', { username, password, client_type: 'admin' });
    if (res?.code === 0) {
      flushSync(() => {
        const expiresIn = res.data?.expires_in || 2592000;
        setToken(res.data.token, expiresIn * 1000);
        // 确保 roles 始终是数组
        setUser({ ...res.data.user, roles: res.data.user?.roles || [] });
      });
    } else {
      throw new Error(res?.message || t('登录失败'));
    }
  };

  const logout = () => {
    removeToken();
    setUser(null);
    window.location.href = '/login';
  };

  const hasRole = (roles: string[]) => {
    if (!user) return false;
    const userRoles = user.roles || [];
    if (userRoles.includes('admin') || userRoles.includes('ops')) return true;
    return roles.every((r) => userRoles.includes(r));
  };

  const hasAnyRole = (roles: string[]) => {
    if (!user) return false;
    const userRoles = user.roles || [];
    if (userRoles.includes('admin') || userRoles.includes('ops')) return true;
    return roles.some((r) => userRoles.includes(r));
  };

  // 判断是否为管理员
  const isAdmin = () => {
    if (!user) return false;
    const userRoles = user.roles || [];
    return userRoles.includes('admin') || userRoles.includes('ops');
  };

  // 检查菜单权限
  // 优先读 permissions.menus（数据库动态配置）；若后端未下发则按 menuConfig.roles 兜底
  const hasMenuPermission = (menuKey: string) => {
    if (!user) return false;
    const perms = user.permissions;
    if (perms) {
      const menus = perms.menus || [];
      if (menus.includes('*')) return true;
      return menus.includes(menuKey);
    }
    // 兜底：permissions 未下发时，admin/ops 有全部权限，其他角色允许访问所有菜单（避免黑屏）
    const userRoles = user.roles || [];
    if (userRoles.includes('admin') || userRoles.includes('ops')) return true;
    // 非 admin 角色但无 permissions 配置时，按宽松策略放行（让用户至少能看到菜单，后续再由管理员配置权限）
    return userRoles.length > 0;
  };

  // 检查按钮/操作权限
  const hasButtonPermission = (buttonCode: string) => {
    if (!user) return false;
    const perms = user.permissions;
    if (!perms) return false;
    const buttons = perms.buttons || [];
    if (buttons.includes('*')) return true;
    return buttons.includes(buttonCode);
  };

  // 根据订单状态和角色判断是否可以查看订单
  const canViewOrder = (status: number) => {
    if (!user) return false;
    if (isAdmin()) return true;
    const userRoles = user.roles || [];

    // 业务员、业务经理、跟单：不再按门店隔离，可查看全部订单
    if (userRoles.includes('salesperson') || userRoles.includes('sales_manager') || userRoles.includes('clerk')) {
      return true;
    }

    // 技术人员/技术经理：只能看到需要复审的状态 (122)
    if (userRoles.includes('technician') || userRoles.includes('tech_manager')) {
      return [122].includes(status);
    }

    // 财务人员：可以看到待初审及之后的状态
    if (userRoles.includes('finance')) {
      return [121, 122, 123, 130, 140, 150, 160, 170].includes(status);
    }

    // 采购专员：只能看到已审核及之后的状态
    if (userRoles.includes('purchaser')) {
      return status >= 130;
    }

    // 工厂：可见所有关联自己工厂的订单（后端已按 factory_id 过滤）
    if (userRoles.includes('factory')) {
      return true;
    }

    // 门店端用户：只能看到自己门店的订单（后端已过滤，前端兜底）
    if (userRoles.includes('store_customer')) {
      return true; // 后端接口会自动按store_id过滤
    }

    return false;
  };

  // 是否可以进行初审（状态 121）
  const canReviewFirst = () => {
    return hasButtonPermission('order:review_first');
  };

  // 是否可以进行复审（状态 122）
  const canReviewSecond = () => {
    return hasButtonPermission('order:review_second');
  };

  // 是否可以进行财务审核（状态 123）
  const canReviewFinance = () => {
    return hasButtonPermission('order:review_finance');
  };

  // 是否可以推送工厂（状态 130 -> 140）
  const canPushToFactory = () => {
    return hasButtonPermission('order:push');
  };

  // 是否可以发货（状态 140 -> 150）
  const canShipOrder = () => {
    return hasButtonPermission('order:ship');
  };

  // 是否可以完成订单（状态 150 -> 160）——后台代确认
  const canCompleteOrder = () => {
    return hasButtonPermission('order:complete');
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      logout,
      hasRole,
      hasAnyRole,
      hasMenuPermission,
      hasButtonPermission,
      canViewOrder,
      canReviewFirst,
      canReviewSecond,
      canReviewFinance,
      canPushToFactory,
      canShipOrder,
      canCompleteOrder,
      isAdmin,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
